/**
 * Agent Memory Definitions list — `GET /v1/memory/definitions`.
 *
 * The admin surface of the Memory module (spec §2A.7). It never lists memory *contents* — that is
 * `MemoryListPage`. An AMD is versioned: a change is a new row, never an update, so the same
 * `agentKey` appears once per version and exactly one of them carries `isCurrent`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference):
 * `ShellHeader`, `CordioAbpPagedDataTable`, and a `.memory-definitions` BEM block. The grid owns
 * paging, search and filtering; `listVersion` is the one piece of refetch state left, bumped after
 * a bulk apply so `dataSource`'s identity changes and the grid reloads.
 *
 * WHY every filter is applied in this file rather than sent to the API: `listDefinitions()` takes
 * **no arguments at all** — no page, no search, no status. The endpoint returns the tenant's whole
 * definition set, so search / status / current-version filtering and paging are done here, over
 * that response. {@link applyDefinitionFilters} and {@link toClientPage} are that work; the ABP
 * offset→page arithmetic still belongs to `createCortexPagedDataSource`.
 *
 * WHY the rows carry a synthetic `id`: the wire DTO's key is `agentMemoryDefinitionId`, but both
 * the grid and `createCortexPagedDataSource` require `{ id: string }` (the grid keys rows and
 * read-more state on it). {@link toRow} adds it without touching the service or the DTO.
 *
 * Capabilities preserved outside the grid:
 *   - Bulk apply — clone one definition onto agents that have none. It is a per-row action with a
 *     free-text list of agent ids and a two-number outcome ("applied to N, skipped M"), which no
 *     grid cell can express, so it stays a dialog. Skips are the point: cutover is per-AMD and
 *     never overwrites an existing definition.
 *
 * --- Column plan -------------------------------------------------------------------------------
 * field        | header  | width | cell type              | filter
 * -------------|---------|-------|------------------------|--------------------------------------
 * displayName  | Name    | 24%   | text, clickable        | toolbar search (client-side)
 * agentKey     | Agent   | 24%   | text                   | toolbar search (client-side)
 * version      | Version | 10%   | text ("v3 (current)")  | status tabs -> `isCurrent`
 * status       | Status  | 14%   | custom (lifecycle Chip)| select `status` (1-4)
 * taskName     | Task    | 16%   | text                   | toolbar search (client-side)
 * createdAtUtc | Created | 12%   | date, locale           | —
 *
 * Dropped from the pre-migration table:
 *   - Scope slots (a chip per declared scope dimension) — up to four chips per row, and the slots
 *     only mean anything next to the values that fill them, which is the definition editor's job.
 *   - Token budgets (instructions / episodic) — two numbers that are tuning knobs, not identity;
 *     they belong beside the other retrieval settings in the editor.
 *   - The task name's old home as a subtitle under Name; it is its own column now.
 *
 * Status tabs use `isCurrent`, not the lifecycle `status`. WHY: the tab's test id is built from its
 * `value` (`cordio-datatable-status-tab-<value>`) and GRID-13 selects `-all` / `-true` / `-false`,
 * so the tab dimension has to be genuinely boolean. `isCurrent` is — and "which version is live"
 * is the first question this list gets asked. The four-value lifecycle ladder is a popover filter.
 * -----------------------------------------------------------------------------------------------
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Chip,
  CordioAbpPagedDataTable,
  ShellHeader,
  Textarea,
  type ChipColor,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { Plus } from 'lucide-react';

import {
  createCortexPagedDataSource,
  type CortexPagedQuery,
} from '../components/data-display/cortexPagedDataSource';
import { memoryModuleService } from '../services/memoryModuleService';
import { useNotificationStore } from '../store/notificationStore';
import {
  LifecycleStatus,
  lifecycleStatusLabel,
  type AgentMemoryDefinitionDto,
} from '../types/memoryModule';
import { ROUTES } from '../utils/routes';

import type { PagedResult } from '../types/tenant';

import './MemoryDefinitionsPage.scss';

/** The wire DTO plus the `id` the grid and the paged data source require. */
type MemoryDefinitionRow = AgentMemoryDefinitionDto & { id: string };

const toRow = (dto: AgentMemoryDefinitionDto): MemoryDefinitionRow => ({
  ...dto,
  id: dto.agentMemoryDefinitionId,
});

/**
 * Quick-filter tabs over "is this the live version?".
 *
 * `value` is load-bearing twice: the grid writes it to the toolbar's `paramKey`, AND it builds each
 * tab's test id as `cordio-datatable-status-tab-<value>`. An All tab declared with an empty value
 * would emit `cordio-datatable-status-tab-` and GRID-13 would select nothing — hence the literal
 * `'all'`, and hence {@link STATUS_TAB_PARAM_KEY} being synthetic.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Current', value: 'true' },
  { label: 'Superseded', value: 'false' },
] as const;

/** The synthetic toolbar key the tabs write to. Mapped to `isCurrent` before any row is filtered. */
const STATUS_TAB_PARAM_KEY = 'current';

/** The lifecycle ladder, as popover filter options. Labels come from the module's own vocabulary. */
const LIFECYCLE_OPTIONS = [
  { label: lifecycleStatusLabel(LifecycleStatus.Draft), value: String(LifecycleStatus.Draft) },
  { label: lifecycleStatusLabel(LifecycleStatus.Active), value: String(LifecycleStatus.Active) },
  {
    label: lifecycleStatusLabel(LifecycleStatus.Deprecated),
    value: String(LifecycleStatus.Deprecated),
  },
  { label: lifecycleStatusLabel(LifecycleStatus.Retired), value: String(LifecycleStatus.Retired) },
] as const;

/** Chip colour per lifecycle rung: live is green, unpublished is amber, withdrawn is red. */
function lifecycleChipColor(status: number): ChipColor {
  switch (status) {
    case LifecycleStatus.Active:
      return 'green';
    case LifecycleStatus.Draft:
      return 'amber';
    case LifecycleStatus.Retired:
      return 'red';
    default:
      return 'default';
  }
}

/** Reads a toolbar filter the grid stores as a string back into a finite number. */
function asFiniteNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Applies the toolbar's search, status tab and lifecycle filter to the full definition set.
 *
 * This is where the synthetic {@link STATUS_TAB_PARAM_KEY} is resolved: `'true'` / `'false'` become
 * an `isCurrent` test and All becomes "no filter at all" rather than a third state.
 */
function applyDefinitionFilters(
  rows: readonly MemoryDefinitionRow[],
  query: CortexPagedQuery,
): readonly MemoryDefinitionRow[] {
  let out = rows;

  const current = query[STATUS_TAB_PARAM_KEY];
  if (current === 'true' || current === 'false') {
    out = out.filter((row) => row.isCurrent === (current === 'true'));
  }

  const status = asFiniteNumber(query.status);
  if (status !== undefined) {
    out = out.filter((row) => row.status === status);
  }

  if (typeof query.searchTerm === 'string' && query.searchTerm.trim() !== '') {
    const needle = query.searchTerm.trim().toLowerCase();
    out = out.filter(
      (row) =>
        row.displayName?.toLowerCase().includes(needle) ||
        row.agentKey?.toLowerCase().includes(needle) ||
        row.taskName?.toLowerCase().includes(needle),
    );
  }

  return out;
}

/**
 * Wraps an unpaged array response as the one page the grid asked for.
 *
 * `totalCount` is exact here — `listDefinitions()` returns the whole set, so the client knows the
 * true total even though the endpoint never says it.
 */
function toClientPage<T>(items: readonly T[], pageNumber: number, pageSize: number): PagedResult<T> {
  const start = Math.max(0, (pageNumber - 1) * pageSize);
  return {
    items: items.slice(start, start + pageSize) as T[],
    totalCount: items.length,
    pageNumber,
    pageSize,
    totalPages: Math.max(1, Math.ceil(items.length / pageSize)),
    hasPreviousPage: pageNumber > 1,
    hasNextPage: start + pageSize < items.length,
  };
}

export const MemoryDefinitionsPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  /** Bumped after a bulk apply to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  /** The definition being cloned, or null when the bulk-apply dialog is closed. */
  const [bulkSource, setBulkSource] = useState<MemoryDefinitionRow | null>(null);
  const [bulkAgentIds, setBulkAgentIds] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkDialogRef = useRef<HTMLDialogElement>(null);

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<MemoryDefinitionRow>(async (query) => {
        const all = (await memoryModuleService.listDefinitions()).map(toRow);
        return toClientPage(applyDefinitionFilters(all, query), query.pageNumber, query.pageSize);
      }),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<MemoryDefinitionRow>>>(
    () => [
      {
        field: 'displayName',
        header: 'Name',
        type: 'text',
        width: '24%',
        clickable: true,
        // A definition can be saved before it is named; say so rather than showing an em dash,
        // which reads as "no such field".
        valueGetter: (row) => row.displayName || '(unnamed)',
      },
      { field: 'agentKey', header: 'Agent', type: 'text', width: '24%', className: 'memory-definitions__key' },
      {
        field: 'version',
        header: 'Version',
        type: 'text',
        width: '10%',
        // The live version has to be legible at a glance even when the Current tab is not selected.
        valueGetter: (row) => `v${row.version}${row.isCurrent ? ' (current)' : ''}`,
      },
      {
        field: 'status',
        header: 'Status',
        type: 'custom',
        width: '14%',
        customCell: (row) => (
          <Chip label={lifecycleStatusLabel(row.status)} color={lifecycleChipColor(row.status)} />
        ),
      },
      { field: 'taskName', header: 'Task', type: 'text', width: '16%', maxLength: 60, showReadMore: true },
      { field: 'createdAtUtc', header: 'Created', type: 'date', width: '12%', dateFormat: 'locale' },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search by name, agent key or task' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Definition version filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [{ key: 'status', label: 'Lifecycle status', type: 'select', options: [...LIFECYCLE_OPTIONS] }],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const openBulkApply = useCallback((row: MemoryDefinitionRow) => {
    setBulkAgentIds('');
    setBulkSource(row);
  }, []);

  const closeBulkApply = useCallback(() => {
    if (bulkBusy) return;
    setBulkSource(null);
    setBulkAgentIds('');
  }, [bulkBusy]);

  // Keep the native dialog's open state in step with `bulkSource`, so Escape and the Cancel button
  // both settle in the same place.
  useEffect(() => {
    const el = bulkDialogRef.current;
    if (!el) return;
    if (bulkSource && !el.open) el.showModal();
    if (!bulkSource && el.open) el.close();
  }, [bulkSource]);

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<MemoryDefinitionRow>>>(
    () => [
      {
        icon: 'square-pen',
        tooltip: 'Open editor',
        onClick: (row) => navigate(`${ROUTES.MEMORY_DEFINITIONS}/${row.id}`),
      },
      {
        icon: 'copy',
        tooltip: 'Bulk-apply to other agents',
        onClick: (row) => openBulkApply(row),
      },
    ],
    [navigate, openBulkApply],
  );

  const handleBulkApply = useCallback(async () => {
    if (!bulkSource) return;

    const agentDefinitionIds = bulkAgentIds
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);

    if (agentDefinitionIds.length === 0) {
      addNotification('Enter at least one agent definition id', 'error');
      return;
    }

    setBulkBusy(true);
    try {
      const result = await memoryModuleService.bulkApply(bulkSource.agentMemoryDefinitionId, {
        agentDefinitionIds,
      });
      addNotification(
        `Applied to ${result.created.length} agent(s); ${result.skipped.length} skipped (already configured)`,
        'success',
      );
      setBulkSource(null);
      setBulkAgentIds('');
      setListVersion((v) => v + 1);
    } catch (err) {
      addNotification(err instanceof Error ? err.message : 'Bulk apply failed', 'error');
    } finally {
      setBulkBusy(false);
    }
  }, [bulkSource, bulkAgentIds, addNotification]);

  return (
    <div className="memory-definitions" data-testid="memory-definition-list-page">
      <ShellHeader
        title="Memory Definitions"
        description="Agent Memory Definitions (AMDs): per-agent memory configuration, versioned and approved. Agents without an AMD keep today's behaviour."
        data-testid="memory-definition-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-memory-definition-btn"
            onClick={() => navigate(ROUTES.MEMORY_DEFINITION_NEW)}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            New Definition
          </Button>
        }
      />

      <div data-testid="memory-definition-list-grid">
        <CordioAbpPagedDataTable<MemoryDefinitionRow>
          className="memory-definitions__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(`${ROUTES.MEMORY_DEFINITIONS}/${row.id}`)}
        />
      </div>

      {/* Bulk apply. Not a DeleteDialog: it is additive, and it needs a free-text field. */}
      <dialog
        ref={bulkDialogRef}
        className="memory-definitions__bulk-dialog"
        aria-labelledby="bulk-apply-title"
        data-testid="bulk-apply-dialog"
        onClose={closeBulkApply}
      >
        <div className="memory-definitions__bulk-panel">
          <h2 id="bulk-apply-title" className="memory-definitions__bulk-title">
            Bulk-apply “{bulkSource?.displayName || '(unnamed)'}”
          </h2>
          <p className="memory-definitions__bulk-description">
            Clones this definition onto agents that have none. Agents with an existing definition are
            skipped — cutover is per-AMD and never overwrites.
          </p>
          <label className="memory-definitions__bulk-label" htmlFor="bulk-apply-agent-ids">
            Agent definition ids (comma, semicolon or newline separated)
          </label>
          <Textarea
            id="bulk-apply-agent-ids"
            rows={4}
            value={bulkAgentIds}
            onChange={(e) => setBulkAgentIds(e.target.value)}
            data-testid="bulk-apply-agent-ids"
          />
          <div className="memory-definitions__bulk-actions">
            <Button
              type="button"
              variant="outline"
              disabled={bulkBusy}
              onClick={closeBulkApply}
              data-testid="bulk-apply-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              disabled={bulkBusy}
              aria-busy={bulkBusy}
              onClick={() => void handleBulkApply()}
              data-testid="bulk-apply-confirm"
            >
              {bulkBusy ? 'Applying…' : 'Apply'}
            </Button>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default MemoryDefinitionsPage;
